import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Avatar,
  CardMedia,
  Link,
  Container,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  FitnessCenter as FitnessCenterIcon,
  PlayArrow as PlayArrowIcon,
  Build as EquipmentIcon,
  Info as InfoIcon,
  Assignment as InstructionsIcon,
  Psychology as TipsIcon,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ROUTES } from "../../../constants";
import { useAuth } from "../../auth/hooks/useAuth";
import { useExercisesMap } from "../hooks/useExercisesMap";
import NewColors from "../../../shared/theme/NewColors";

const printStyles = `
  @media print {
    .no-print-videos { display: none !important; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

function getYouTubeId(url) {
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

function formatEquipmentName(equipment) {
  return equipment
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

function formatMuscleGroup(mg) {
  return (mg || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function ExerciseDetailsPage() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { organizationId } = useAuth();
  const { findById, loading, error } = useExercisesMap(organizationId, !!organizationId);

  const exercise = findById(exerciseId);

  const handleBack = () => {
    navigate(ROUTES.TRAINING_PLAN);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error.message}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Training Plan
        </Button>
      </Container>
    );
  }

  if (!exercise) {
    return (
      <Container maxWidth="lg">
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Exercise not found.
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Training Plan
        </Button>
      </Container>
    );
  }

  const primaryColor = theme.palette.primary.main;

  return (
    <>
      <style>{printStyles}</style>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Box sx={{ mb: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ mb: 2 }}
              color="primary"
            >
              Back to Training Plan
            </Button>

            {/* Exercise Header */}
            <Card elevation={2} sx={{ mb: 3 }}>
              <Box
                sx={{
                  backgroundColor: primaryColor,
                  color: "white",
                  p: { xs: 2, sm: 3 },
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                  }}
                >
                  <FitnessCenterIcon fontSize="large" />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {exercise.name}
                  </Typography>
                  <Typography variant="h6" component="h2" sx={{ opacity: 0.9 }}>
                    Exercise Details
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>

          <Grid container spacing={3}>
            {/* Main Content */}
            <Grid item xs={12} md={8}>
              {/* Description */}
              <Card elevation={1} sx={{ mb: 3 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <InfoIcon color="primary" />
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600 }}
                    >
                      Description
                    </Typography>
                  </Stack>
                  {exercise.description ? (
                    <Box sx={{ "& p": { mb: 2 }, "& ul, & ol": { pl: 3, mb: 2 } }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {exercise.description}
                      </ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No description available.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Instructions */}
              <Card elevation={1} sx={{ mb: 3 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <InstructionsIcon color="primary" />
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600 }}
                    >
                      Instructions
                    </Typography>
                  </Stack>
                  {exercise.instructions ? (
                    <Box sx={{ "& p": { mb: 2 }, "& ul, & ol": { pl: 3, mb: 2 } }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {exercise.instructions}
                      </ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No instructions available.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Videos - hidden when printing */}
              <Card elevation={1} sx={{ mb: 3 }} className="no-print-videos">
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <PlayArrowIcon color="primary" />
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600 }}
                    >
                      How-to Videos
                    </Typography>
                  </Stack>
                  {exercise.videos && exercise.videos.length > 0 ? (
                    <Grid container spacing={2}>
                      {exercise.videos.map((video, videoIndex) => {
                        const videoId = getYouTubeId(video);

                        return (
                          <Grid item xs={12} sm={6} key={videoIndex}>
                            <Card variant="outlined" sx={{ position: "relative" }}>
                              {videoId ? (
                                <>
                                  <CardMedia
                                    component="img"
                                    sx={{ height: { xs: 160, sm: 180 }, objectFit: "cover" }}
                                    image={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                    alt={`${exercise.name} demonstration`}
                                    onError={(e) => {
                                      e.target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 56,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      backgroundColor: "rgba(0,0,0,0.3)",
                                      cursor: "pointer",
                                      borderRadius: "4px 4px 0 0",
                                    }}
                                    onClick={() => window.open(video, "_blank")}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: "rgba(255,255,255,0.9)",
                                        color: "primary.main",
                                        width: 64,
                                        height: 64,
                                      }}
                                    >
                                      <PlayArrowIcon fontSize="large" />
                                    </Avatar>
                                  </Box>
                                  <CardContent sx={{ textAlign: "center" }}>
                                    <Link
                                      href={video}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      underline="none"
                                    >
                                      <Button variant="contained" fullWidth>
                                        Watch on YouTube
                                      </Button>
                                    </Link>
                                  </CardContent>
                                </>
                              ) : (
                                <CardContent sx={{ textAlign: "center" }}>
                                  <PlayArrowIcon
                                    color="primary"
                                    sx={{ fontSize: 48, mb: 2 }}
                                  />
                                  <Link
                                    href={video}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="none"
                                  >
                                    <Button variant="outlined" fullWidth>
                                      View Video Tutorial
                                    </Button>
                                  </Link>
                                </CardContent>
                              )}
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No videos available.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Images */}
              <Card elevation={1} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 3 }}
                  >
                    Exercise Images
                  </Typography>
                  {exercise.images && exercise.images.length > 0 ? (
                    <Grid container spacing={2}>
                      {exercise.images.map((image, imageIndex) => (
                        <Grid item xs={12} sm={6} md={4} key={imageIndex}>
                          <Card variant="outlined">
                            <CardMedia
                              component="img"
                              image={image}
                              alt={`${exercise.name} demonstration ${imageIndex + 1}`}
                              sx={{ height: { xs: 180, sm: 200 }, objectFit: "cover" }}
                            />
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No images available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              {/* Difficulty */}
              {exercise.difficulty && (
                <Card elevation={1} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Difficulty
                    </Typography>
                    <Chip
                      label={formatEquipmentName(exercise.difficulty)}
                      color="primary"
                      variant="outlined"
                    />
                  </CardContent>
                </Card>
              )}

              {/* Muscle Groups */}
              {exercise.muscleGroups && exercise.muscleGroups.length > 0 && (
                <Card elevation={1} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      Muscle Groups
                    </Typography>
                    <Stack spacing={1}>
                      {exercise.muscleGroups.map((mg, idx) => (
                        <Chip
                          key={idx}
                          label={formatMuscleGroup(mg)}
                          color="primary"
                          variant="outlined"
                          sx={{ justifyContent: "flex-start" }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Equipment */}
              {exercise.equipment && exercise.equipment.length > 0 && (
                <Card elevation={1} sx={{ mb: 3 }}>
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <EquipmentIcon color="primary" />
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ fontWeight: 600 }}
                      >
                        Equipment Needed
                      </Typography>
                    </Stack>

                    <Stack spacing={1}>
                      {exercise.equipment.map((equip, equipIndex) => (
                        <Chip
                          key={equipIndex}
                          label={formatEquipmentName(equip)}
                          color="primary"
                          variant="outlined"
                          sx={{ justifyContent: "flex-start" }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Quick Tips */}
              <Card elevation={1} sx={{ bgcolor: NewColors.sectionBackground }}>
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <TipsIcon color="primary" />
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 600 }}
                    >
                      Quick Tips
                    </Typography>
                  </Stack>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      • Focus on proper form over heavy weight
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Control the movement throughout the full range of motion
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Breathe consistently during the exercise
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Stop if you feel any pain or discomfort
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
}
